import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import G2Logo from '../../../../assets/g2-t.png';
import { track } from '../../../utils/analytics';
import { getLocalStorageItem, setLocalStorageItem } from '../../../utils/safe-storage';

const StyledDialogContent = styled(DialogContent)`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 40px !important;
	max-width: 480px;
	text-align: center;
`;

const Logo = styled.img`
	width: 64px;
	height: 64px;
	margin-bottom: 24px;
`;

const Title = styled(Typography)`
	font-size: 24px !important;
	font-weight: 700 !important;
	margin-bottom: 16px !important;
`;

const Subtitle = styled(Typography)`
	font-size: 16px !important;
	margin-bottom: 24px !important;
`;

const TimeToComplete = styled(Typography)`
	font-size: 14px !important;
	color: ${(props) => props.theme.palette.text.secondary};
	margin-bottom: 24px !important;
`;

const ButtonContainer = styled(Box)`
	display: flex;
	gap: 16px;
	margin-bottom: 16px;
`;

const G2_REVIEW_STORAGE_KEY = 'g2-review-prompt';

const G2ReviewStates = {
	DISMISSED: 'dismissed',
	REVIEW_STARTED: 'review-started',
};

/**
 * Check if the G2 review modal should be shown based on localStorage
 * @returns {boolean} True if modal should be shown, false otherwise
 */
export const shouldShowG2ReviewModal = (orgRequestsCreated) => {
	if (orgRequestsCreated < 10) {
		return false;
	}

	const g2ReviewStatus = getLocalStorageItem(G2_REVIEW_STORAGE_KEY);

	if (!g2ReviewStatus) {
		return true; // First time user, show modal
	}

	if (g2ReviewStatus === G2ReviewStates.REVIEW_STARTED) {
		return false; // User started review process, don't show again
	}

	// Check if remind later date or dismissed date has passed
	try {
		const futureDate = new Date(g2ReviewStatus);
		const now = new Date();
		return now >= futureDate;
	} catch (error) {
		// If date parsing fails, show modal
		return true;
	}
};

/**
 * Set the G2 review modal state to dismissed (user clicked "No thanks")
 * Now sets a future date in 3 months instead of permanently dismissing
 */
const setG2ReviewDismissed = () => {
	const dismissDate = new Date();
	dismissDate.setMonth(dismissDate.getMonth() + 3); // 3 months from now
	setLocalStorageItem(G2_REVIEW_STORAGE_KEY, dismissDate.toISOString());
	track('G2 Review: Not Interested', {
		action: 'not_interested',
		timestamp: new Date().toISOString(),
	});
};

/**
 * Set the G2 review modal state to remind later (user clicked "Remind me later")
 * @param {number} days - Number of days to wait before showing again (default: 7)
 */
const setG2ReviewRemindLater = (days = 7) => {
	const remindLaterDate = new Date();
	remindLaterDate.setDate(remindLaterDate.getDate() + days);
	setLocalStorageItem(G2_REVIEW_STORAGE_KEY, remindLaterDate.toISOString());

	track('G2 Review: Remind Later', {
		action: 'remind_later',
		days,
		timestamp: new Date().toISOString(),
	});
};

/**
 * Set the G2 review modal state to review started (user clicked "Write a Review")
 */
const setG2ReviewStarted = () => {
	setLocalStorageItem(G2_REVIEW_STORAGE_KEY, G2ReviewStates.REVIEW_STARTED);
	track('G2 Review: Write Review Started', {
		action: 'write_review_started',
		timestamp: new Date().toISOString(),
	});
};

/**
 * Track when the G2 review modal is opened
 */
const trackG2ReviewModalOpened = () => {
	track('G2 Review: Modal Opened', {
		action: 'modal_opened',
		timestamp: new Date().toISOString(),
	});
};

const G2ReviewModal = ({ open, onClose }) => {
	const handleWriteReview = () => {
		setG2ReviewStarted();
		onClose();
	};

	const handleRemindLater = () => {
		setG2ReviewRemindLater(7); // Remind in 7 days
		onClose();
	};

	const handleNotInterested = () => {
		setG2ReviewDismissed();
		onClose();
	};

	const handleClose = (event, reason) => {
		if (reason === 'backdropClick') return;

		onClose && onClose();
	};

	// Track when modal is opened
	useEffect(() => {
		if (open) {
			trackG2ReviewModalOpened();
		}
	}, [open]);

	const containerRef = useRef(null);

	useEffect(() => {
		// Prevent multiple script loads if component mounts more than once
		const existingScript = document.querySelector(
			'script[src="https://www.g2.com/assets/write_a_review_entry.js"]'
		);
		if (existingScript) {
			// If script already exists, just re-run the G2 function
			if (window.g2) {
				window.g2('write-a-review', {
					productSlug: 'refnow',
					host: 'https://www.g2.com',
					element: containerRef.current,
					buttonText: 'Write a G2 Review',
					buttonColor: '#5A3FF5',
					buttonHoverColor: '#BBB3FA',
					buttonTextColor: '#fff',
				});
			}
			return;
		}

		// Define the global function
		window.G2WriteAReview = 'g2';
		window.g2 =
			window.g2 ||
			function () {
				(window.g2.q = window.g2.q || []).push(arguments);
			};

		// Load the script
		const script = document.createElement('script');
		script.src = 'https://www.g2.com/assets/write_a_review_entry.js';
		script.async = true;
		script.defer = true;

		script.onload = () => {
			window.g2('write-a-review', {
				productSlug: 'refnow',
				host: 'https://www.g2.com',
				element: containerRef.current,
				buttonText: 'Write a G2 Review',
				buttonColor: '#5A3FF5',
				buttonHoverColor: '#BBB3FA',
				buttonTextColor: '#fff',
			});
		};

		document.head.appendChild(script);
	}, []);

	return (
		<Dialog open={open} onClose={handleClose} disableEscapeKeyDown>
			<StyledDialogContent>
				<Logo src={G2Logo} alt="G2 Logo" />
				<Title variant="h1">
					<FormattedMessage
						id="g2Review.title"
						defaultMessage="Share a review and earn £15 GBP"
					/>
				</Title>
				<Subtitle variant="body1">
					<FormattedMessage
						id="g2Review.subtitle"
						defaultMessage="Share your experience with RefNow on G2 and receive a gift card upon approval."
					/>
				</Subtitle>
				<TimeToComplete variant="caption">
					<FormattedMessage
						id="g2Review.timeToComplete"
						defaultMessage="Approximate time to complete: < 5 mins"
					/>
				</TimeToComplete>
				<ButtonContainer>
					<Button variant="outlined" onClick={handleRemindLater}>
						<FormattedMessage
							id="g2Review.remindLater"
							defaultMessage="Remind me later"
						/>
					</Button>
					<Box
						id="g2-write-a-review-container"
						ref={containerRef}
						onClick={handleWriteReview}
					>
						<FormattedMessage
							id="g2Review.writeReview"
							defaultMessage="Write a Review"
						/>
					</Box>
				</ButtonContainer>
				<Button
					variant="text"
					onClick={handleNotInterested}
					sx={{ textTransform: 'none', fontSize: '14px' }}
				>
					<FormattedMessage
						id="g2Review.notInterested"
						defaultMessage="No thanks, I'll pass on £15"
					/>
				</Button>
			</StyledDialogContent>
		</Dialog>
	);
};

export default G2ReviewModal;
